import React, { useEffect, useRef, useState } from 'react';
import { StageScreen, StagePopup, popupHasContent } from './components/StageShell';
import { s3PublicUrl } from '../../utils/s3PublicUrl';

// Earth is decorative only (matches legacy stage2.php — only Venus/Moon/Mars
// were clickable there). It still renders with the idle spaceship on it.
const EARTH = { key: 'earth', imgKey: 'earth_image', msgKey: 'earth_message' };
const CLICKABLE_PLANETS = [
  { key: 'venus', imgKey: 'venus_image', msgKey: 'venus_message' },
  { key: 'moon', imgKey: 'moon_image', msgKey: 'moon_message' },
  { key: 'mars', imgKey: 'mars_image', msgKey: 'mars_message' },
];

// Full-scene launch gifs (legacy stage2.php) — rocket flies from Earth and
// lands on the chosen planet. Bundled under public/future/stage2/.
const PUBLIC = process.env.PUBLIC_URL || '';
const LAUNCH_GIFS = {
  venus: `${PUBLIC}/future/stage2/venus.gif`,
  moon: `${PUBLIC}/future/stage2/moon.gif`,
  mars: `${PUBLIC}/future/stage2/mars.gif`,
};

const GIF_LAUNCH_DURATION_MS = 5200;
const CSS_LAND_DURATION_MS = 2000;
const MSG_HIDE_DELAY_MS = 180;

export default function Stage2Planets({ stage, pagesBg, onComplete }) {
  const startHasContent = popupHasContent(stage?.start_popup_image, stage?.start_popup_text);
  const endHasContent = popupHasContent(stage?.end_popup_image, stage?.end_popup_text);
  const [phase, setPhase] = useState(startHasContent ? 'start' : 'game');
  const [activeMsg, setActiveMsg] = useState(null);
  const [launching, setLaunching] = useState(null);
  const [picked, setPicked] = useState(null);
  const [gifFailed, setGifFailed] = useState({});
  const launchTimeoutRef = useRef(null);
  const hideMsgTimerRef = useRef(null);

  useEffect(() => () => {
    if (launchTimeoutRef.current) clearTimeout(launchTimeoutRef.current);
    if (hideMsgTimerRef.current) clearTimeout(hideMsgTimerRef.current);
  }, []);

  const finishStage = () => onComplete({ picked });

  const gifFor = (key) => {
    const src = LAUNCH_GIFS[key];
    if (!src || gifFailed[key]) return null;
    return src;
  };

  const showMessage = (key) => {
    if (picked) return;
    if (hideMsgTimerRef.current) {
      clearTimeout(hideMsgTimerRef.current);
      hideMsgTimerRef.current = null;
    }
    setActiveMsg(key);
  };

  const hideMessage = (key) => {
    if (picked) return;
    if (hideMsgTimerRef.current) clearTimeout(hideMsgTimerRef.current);
    hideMsgTimerRef.current = setTimeout(() => {
      setActiveMsg((prev) => (prev === key ? null : prev));
      hideMsgTimerRef.current = null;
    }, MSG_HIDE_DELAY_MS);
  };

  const handlePlanetClick = (key) => {
    if (picked) return;
    if (hideMsgTimerRef.current) {
      clearTimeout(hideMsgTimerRef.current);
      hideMsgTimerRef.current = null;
    }
    setPicked(key);
    setActiveMsg(null);
    setLaunching(key);
    if (launchTimeoutRef.current) clearTimeout(launchTimeoutRef.current);
    const holdMs = gifFor(key) ? GIF_LAUNCH_DURATION_MS : CSS_LAND_DURATION_MS;
    launchTimeoutRef.current = setTimeout(() => {
      setLaunching(null);
      if (endHasContent) setPhase('end');
      else onComplete({ picked: key });
    }, holdMs);
  };

  if (phase === 'start') {
    return (
      <StageScreen pagesBg={pagesBg} pageKey="stage_2" stage={stage}>
        <StagePopup image={stage?.start_popup_image} text={stage?.start_popup_text} onNext={() => setPhase('game')} />
      </StageScreen>
    );
  }

  if (phase === 'end') {
    const pickedAnswer = picked ? picked.charAt(0).toUpperCase() + picked.slice(1) : '';
    return (
      <StageScreen pagesBg={pagesBg} pageKey="stage_2" stage={stage}>
        <StagePopup
          image={stage?.end_popup_image}
          boldText={pickedAnswer}
          text={stage?.end_popup_text}
          onNext={finishStage}
        />
      </StageScreen>
    );
  }

  const heading = String(stage?.heading ?? '').trim();
  const subheading = String(stage?.subheading ?? '').trim();
  const spaceshipImg = s3PublicUrl(stage?.spaceship_image);
  const earthImg = s3PublicUrl(stage?.[EARTH.imgKey]);
  const activePlanet = CLICKABLE_PLANETS.find((p) => p.key === activeMsg);
  const activeMessage = activePlanet
    ? String(stage?.[activePlanet.msgKey] ?? '').trim()
    : '';
  const launchGifSrc = launching ? gifFor(launching) : null;
  // Mobile (and gif-fallback): land the spaceship sprite on the chosen planet.
  const useCssLand = Boolean(launching && !launchGifSrc && spaceshipImg);

  return (
    <StageScreen pagesBg={pagesBg} pageKey="stage_2" stage={stage}>
      <div
        className={`fi-stage-root fi-s2-root${activeMsg && !launching ? ' fi-s2-root--msg-open' : ''}${launching ? ' fi-s2-root--launching' : ''}`}
      >
        {heading ? <h2 className="fi-heading">{heading}</h2> : null}
        {subheading ? <p className="fi-stage-subheading">{subheading}</p> : null}
        <div className={`fi-planets-stage${launchGifSrc ? ' fi-planets-stage--gif-launch' : ''}${useCssLand ? ' fi-planets-stage--css-land' : ''}`}>
          {spaceshipImg && !launching && !picked ? (
            <img className="fi-spaceship-idle" src={spaceshipImg} alt="" aria-hidden />
          ) : null}

          {launchGifSrc ? (
            <img
              key={launching}
              className={`fi-planet-gif fi-planet-gif--${launching}`}
              src={launchGifSrc}
              alt=""
              aria-hidden
              onError={() => {
                setGifFailed((prev) => ({ ...prev, [launching]: true }));
              }}
            />
          ) : null}

          <div className="fi-planet fi-planet--earth" aria-hidden>
            {earthImg ? <img className="fi-planet-img" src={earthImg} alt="" /> : null}
          </div>

          {CLICKABLE_PLANETS.map(({ key, imgKey }) => {
            const img = s3PublicUrl(stage?.[imgKey]);
            const isLaunching = launching === key;
            const locked = Boolean(picked) && picked !== key;
            const isHeld = Boolean(launching) && picked !== key;
            const isActive = activeMsg === key && !launching;
            const showCssShip = useCssLand && (isLaunching || picked === key);
            return (
              <div
                key={key}
                className={`fi-planet fi-planet--${key}${locked || isHeld ? ' fi-planet--locked' : ''}${isActive ? ' fi-planet--active' : ''}${isLaunching ? ' fi-planet--launching' : ''}`}
              >
                <button
                  type="button"
                  className="fi-planet-btn"
                  disabled={locked}
                  onMouseEnter={() => showMessage(key)}
                  onMouseLeave={() => hideMessage(key)}
                  onFocus={() => showMessage(key)}
                  onBlur={() => hideMessage(key)}
                  onClick={() => handlePlanetClick(key)}
                >
                  {img ? <img className="fi-planet-img" src={img} alt={key} /> : <span>{key}</span>}
                </button>
                {showCssShip ? (
                  <img
                    className={`fi-planet-spaceship fi-planet-spaceship--${key}${!isLaunching && picked === key ? ' fi-planet-spaceship--landed' : ''}`}
                    src={spaceshipImg}
                    alt=""
                    aria-hidden
                  />
                ) : null}
              </div>
            );
          })}

          {activeMsg && activeMessage && !launching ? (
            <>
              <div className="fi-planet-msg-dim" aria-hidden />
              <div
                className={`fi-planet-msg fi-planet-msg--text fi-planet-msg--${activeMsg}`}
                role="status"
                onMouseEnter={() => showMessage(activeMsg)}
                onMouseLeave={() => hideMessage(activeMsg)}
              >
                <div className="fi-planet-msg__title">{activeMsg}</div>
                <div className="fi-planet-msg__body">
                  <p>{activeMessage}</p>
                </div>
              </div>
            </>
          ) : null}
        </div>
      </div>
    </StageScreen>
  );
}
